import React from 'react'
import { t } from 'i18next'
import { Link } from 'react-router-dom'
import { Action, any, async, AsyncState, IOWidget, loadingAsyncState, mk_widget, stateful } from 'widgets-for-react'
import { TemplateState } from '../../../../cms/page/types.gen'
import { CustomBlock } from '../../../../wordpress/types_and_parser/custom_blocks.gen'
import { Block } from '../../../../wordpress/types_and_parser/standard_blocks.gen'
import { WordPress } from '../../../../wordpress/wordpress_api'
import { PublicSite_CustomPageData } from '../../../application.mod'
import { moment } from '../../../utils/moment'
import { CustomBlockData } from '../types.mod'
import { getNewsArticles } from './../../../../wordpress/wordpress_api'

export type BSLNewsOverviewBlockProps = {
  key?: string
  page_size?: number
}

type BSLNewsOverviewBlockState = {
  current_page: number
  articles: AsyncState<WordPress.NewsArticleResponse[]>
}

export type BSLNewsOverviewBlockStyle = Partial<{
  block_classname: string
  inner_classname: string
  title_classname: string
  list_classname: string
  item_classname: string
  item_link_classname: string
  item_label_classname: string
  item_title_classname: string
  item_title_text_classname: string
  item_text_classname: string
  clickable_primary_classname: string
}>

export function BSLNewsOverviewBlock<customBlockData, extra_images>(
  props: BSLNewsOverviewBlockProps,
  options?: { style?: BSLNewsOverviewBlockStyle; key?: string }
): IOWidget<
  TemplateState<CustomBlockData, CustomBlock, Block, extra_images, PublicSite_CustomPageData>,
  Action<TemplateState<CustomBlockData, CustomBlock, Block, extra_images, PublicSite_CustomPageData>>
> {
  const page_size = props.page_size ? props.page_size : 9
  return (s: TemplateState<CustomBlockData, CustomBlock, Block, extra_images, PublicSite_CustomPageData>) =>
    stateful<BSLNewsOverviewBlockState>()(s0 =>
      any<Action<BSLNewsOverviewBlockState>>()([
        async<WordPress.NewsArticleResponse[]>()(s0.articles).map(a => s1 => ({ ...s1, articles: a(s1.articles) })),
        mk_widget({
          run: setState => (
            <div className="block">
              {s0.articles.kind === 'loaded' ? (
                <>
                  <ul className="flex flex--3 grid grid--news">
                    {s0.articles.value
                      .slice(page_size * s0.current_page, page_size * s0.current_page + page_size)
                      .map(article => (
                        <li className="flex-item grid-item">
                          <Link to={`/nieuws/${article.slug}`} className="grid-item__link">
                            <div className="grid-item__content">
                              <h2 className="title--m grid-item__title">{article.title.rendered}</h2>
                              <div className="grid-item__meta">
                                <div>
                                  <svg aria-hidden="true" width="13" height="13">
                                    <use xlinkHref="#icon--clock" />
                                  </svg>
                                  <span>{article.acf.author_name}</span>
                                </div>
                                <div>
                                  <svg aria-hidden="true" width="13" height="13">
                                    <use xlinkHref="#icon--person" />
                                  </svg>
                                  <time dateTime={moment(article.modified).toISOString()}>
                                    {moment(article.date_gmt).format('LL')}
                                  </time>
                                </div>
                              </div>
                              <p dangerouslySetInnerHTML={{ __html: article.acf.hero_intro }} />
                              <span className="link-button list-item__faux-link grid-item__faux-link">Lees meer</span>
                            </div>

                            <div className="grid-item__figure">
                              <span className="grid-item__label">Nieuws</span>
                              {article.acf.image ? (
                                <img
                                  src={article.acf.image.sizes.medium_large}
                                  alt={article.acf.image_alt}
                                  className="grid-item__image"
                                />
                              ) : (
                                <img
                                  src="/frontend/images/bsl-768x376.jpg"
                                  alt="Logo BSL"
                                  className="grid-item__image"
                                />
                              )}
                            </div>
                          </Link>
                        </li>
                      ))}
                  </ul>
                  <div className="buttons">
                    {s0.current_page > 0 && (
                      <button
                        className="link"
                        onClick={_ => setState(s => ({ ...s, current_page: s.current_page - 1 }))}
                      >
                        {t('pagination.newer')}
                      </button>
                    )}
                    {s0.current_page < Math.floor(s0.articles.value.length / page_size) && (
                      <button
                        className="link"
                        onClick={_ => setState(s => ({ ...s, current_page: s.current_page + 1 }))}
                      >
                        {t('pagination.older')}
                      </button>
                    )}
                  </div>
                </>
              ) : (
                <div>
                  <div className="svg-loader svg-loader--nobg">
                    <svg viewBox="0 0 40 40" className="svg-loader__svg">
                      <path
                        fill="#ccc"
                        className="svg-loader__background"
                        d="M20.201,5.169c-8.254,0-14.946,6.692-14.946,14.946c0,8.255,6.692,14.946,14.946,14.946
                            s14.946-6.691,14.946-14.946C35.146,11.861,28.455,5.169,20.201,5.169z M20.201,31.749c-6.425,0-11.634-5.208-11.634-11.634
                            c0-6.425,5.209-11.634,11.634-11.634c6.425,0,11.633,5.209,11.633,11.634C31.834,26.541,26.626,31.749,20.201,31.749z"
                      />
                      <path
                        fill="#000"
                        className="svg-loader__foreground"
                        d="M26.013,10.047l1.654-2.866c-2.198-1.272-4.743-2.012-7.466-2.012h0v3.312h0 C22.32,8.481,24.301,9.057,26.013,10.047z"
                      >
                        <animateTransform
                          attributeType="xml"
                          attributeName="transform"
                          type="rotate"
                          from="0 20 20"
                          to="360 20 20"
                          dur="0.9s"
                          repeatCount="indefinite"
                        />
                      </path>
                    </svg>
                  </div>
                </div>
              )}
            </div>
          )
        })
      ]).map(a => a(s0))
    )({
      articles: loadingAsyncState(() => getNewsArticles()),
      current_page: 0
    }).map(a => s => s)
}
